import React, { useState } from 'react'

import { Button, Modal, ModalInjectedProps } from '@sketch/components'
import {
  SocialSigninDeleteAccountUrlMutationVariables,
  useSocialSigninDeleteAccountUrlMutation,
} from '@sketch/gql-types'
import { useToast } from '@sketch/toasts'

interface AreYouSureModalProps extends ModalInjectedProps {
  provider: SocialSigninDeleteAccountUrlMutationVariables['provider']
}

/**
 * Confirmation modal displayed when a user is about to delete their account.
 * Provides a final warning before proceeding with the irreversible action.
 */
const AreYouSureModal = ({ hideModal, provider }: AreYouSureModalProps) => {
  const { showToast } = useToast()
  const [isPersistentLoading, setIsPersistentLoading] = useState(false)

  const [deleteAccount, { loading: mutationLoading }] =
    useSocialSigninDeleteAccountUrlMutation({
      onError: () => {
        setIsPersistentLoading(false)
        showToast('Something went wrong', 'negative')
      },
      onCompleted: data => {
        const { url } = data.socialSigninDeleteAccountUrl

        /**
         * Safari opens a Apple Sign in Pop-up that does not trigger any event when the user clicks "Cancel"
         * So we need to do this workaround to be able to reset the loading state.
         * When the user clicks cancel the window is focused and we use that to reset the loading state
         */
        const handleFocus = () => {
          setIsPersistentLoading(false)
          window.removeEventListener('focus', handleFocus)
        }

        window.addEventListener('focus', handleFocus)

        window.location.href = url
      },
    })

  const handleDeleteAccount = () => {
    setIsPersistentLoading(true)

    deleteAccount({
      variables: {
        provider,
      },
    })
  }

  const loading = isPersistentLoading || mutationLoading

  return (
    <Modal title="Are you sure?" onCancel={hideModal}>
      <Modal.Body>
        <p>
          Deleting your account will also delete all your Documents. You can’t
          undo this action.
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={hideModal}>
          Cancel
        </Button>
        <Button
          variant="negative"
          onClick={handleDeleteAccount}
          loading={loading}
        >
          Delete Account
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default AreYouSureModal
